// <JoinLanding> — the invite-link landing screen (#join). Opened from a shared link as its
// OWN page (not the home/create page). PURE function of `app`: the classic showJoinLanding()
// stashes the fetched room status on app.landingState and re-mounts; this component derives
// the #joinRoomInfo line + the join/spectate button show-hide from it, exactly as the old
// imperative renderJoinInfo() did — Preact DIFFS instead of clobbering. Emits the SAME ids /
// class names / data-action attrs / visible text as the classic markup so document-level
// click delegation + the smoke grep contract stay green. See PREACT-MIGRATION.md.
//
// The #joinCodeBig text and the #joinLandingName input are read back imperatively by
// joinFromLanding()/startSpectate(); #joinLandingName uses defaultValue (uncontrolled) so
// typed nicknames + a prefilled remembered name both survive re-mounts (same as <Home>).
import { html, rememberedName, t } from '../shared.js';

// The derived #joinRoomInfo facts: human/AI counts, the status line, whether joining is still
// possible (now, or for the next game), and the spectate-button label.
interface DerivedInfo {
  humans: number;
  ai: number;
  txt: string;
  canJoin: boolean;
  /** a game is on: the join button takes a seat in the NEXT game (queueNextGame) */
  next: boolean;
  spectateText: string;
}

// Mirror of the classic renderJoinInfo(): derive the human/AI counts + status line + whether
// joining is still possible from the fetched room state. In the lobby or on the scoreboard you
// join (the scoreboard seats you in the next game); while a game is on you join the next one.
// House agents stand up for people, so only the other seats count toward full.
function deriveInfo(st: LandingState | null | undefined): DerivedInfo | null {
  if (!st || !st.players) return null;
  const humans = st.players.filter((p: Player) => p.type === 'human').length;
  const ai = st.players.length - humans;
  const seats = st.seats || 8;
  const full = st.players.filter((p: Player) => !p.house).length >= seats;
  const now = st.phase === 'lobby' || st.phase === 'ended';
  const phaseText: Partial<Record<Phase, string>> = { lobby: t('join.phaseLobby'), speaking: t('join.phasePlayingNext'), discussion: t('join.phasePlayingNext'), voting: t('join.phasePlayingNext'), ended: t('join.phaseEnded') };
  // A full table can only be spectated — don't invite people to join when they can't.
  const txt = full ? (st.phase === 'lobby' ? t('join.roomFull') : t('join.phasePlaying')) : ((st.phase && phaseText[st.phase]) || '');
  const canJoin = !full;
  const spectateText = full ? t('join.spectateFull') : (st.phase === 'lobby' ? t('join.spectateLobby') : t('join.spectate'));
  return { humans, ai, txt, canJoin, next: canJoin && !now, spectateText };
}

export function JoinLanding({ app }: { app: AppState }) {
  const code = (app && app.landingCode) || '—';
  const info = deriveInfo(app && app.landingState);
  // #joinRoomInfo: loading placeholder until the first fetch lands, then the live counts line.
  // Show TOTAL people in the room (humans + AI) as one figure — never split out the AI count.
  const infoLine = info
    ? html`${t('join.roomCountHumans', { n: info.humans + info.ai })}<br/>${info.txt}`
    : t('join.loading');
  const canJoin = info ? info.canJoin : true;
  const next = !!info && info.next;
  const spectateText = info ? info.spectateText : t('join.spectate');
  // Prefill remembered nickname WITHOUT controlling the input (defaultValue seeds once, Preact
  // never re-clobbers it) so the value joinFromLanding() reads back survives live typing.
  const savedName = rememberedName();
  return html`
    <span class="tag">${t('join.tag')}</span>
    <h2>${t('join.h1')}</h2>
    <p class="muted" style="font-weight:700;line-height:1.5">${t('join.intro')}</p>
    <div class="vibe"><span>${t('join.vibe1')}</span><span>${t('join.vibe2')}</span><span>${t('join.vibe3')}</span><span>${t('join.vibe4')}</span></div>
    <div class="ink-card card stack" style="margin-top:14px">
      <div class="join-code-block"><span class="jc-label">${t('join.codeLabel')}</span><span class="jc-code" id="joinCodeBig">${code}</span></div>
      <div id="joinRoomInfo" class="muted small" style="text-align:center;font-weight:800;line-height:1.5">${infoLine}</div>
      <label class="label">${t('join.nameLabel')}<input id="joinLandingName" placeholder=${t('join.namePlaceholder')} maxlength="12" defaultValue=${savedName}/></label>
      <button class="big yellow" data-action="join-landing" id="joinLandingBtn" style=${canJoin ? '' : 'display:none'}>${next ? t('join.joinNextBtn') : t('join.joinBtn')}</button>
      <button class="ghost" data-action="spectate" id="spectateBtn" style=${canJoin && !next ? 'display:none' : ''}>${spectateText}</button>
      <button class="link-btn" data-action="leave-join">${t('join.backHome')}</button>
    </div>
    <div class="footer">${t('join.footer')}</div>
  `;
}
